// /home/heliowallet/frontend/services/priceService.ts
import axios from 'axios';

interface DefiLlamaPriceResponse {
  coins: {
    [key: string]: {
      decimals: number;
      price: number;
      symbol: string;
      timestamp: number;
      confidence: number;
    };
  };
}

interface CachedPrice {
  price: number | null;
  timestamp: number;
  notFound?: boolean;
}

interface LocalStorageCache {
  [key: string]: CachedPrice;
}

class PriceService {
  private static instance: PriceService;
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly DEFILLAMA_API = 'https://coins.llama.fi';
  private readonly CACHE_KEY = 'helio_price_cache';
  private memoryCache: Map<string, CachedPrice> = new Map();
  
  private constructor() {
    this.loadCacheFromStorage();
  }
  
  static getInstance(): PriceService {
    if (!PriceService.instance) {
      PriceService.instance = new PriceService();
    }
    return PriceService.instance;
  }
  
  // Load cache from localStorage to memory
  private loadCacheFromStorage() {
    // Skip if running on server
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const cache: LocalStorageCache = JSON.parse(stored);
        const now = Date.now();
        
        // Load valid cache entries to memory
        Object.entries(cache).forEach(([key, value]) => {
          if (now - value.timestamp < this.CACHE_DURATION) {
            this.memoryCache.set(key, value);
          }
        });
        
        // Clean up expired entries
        this.saveCache();
      }
    } catch (error) {
      console.error('Failed to load price cache:', error);
    }
  }
  
  // Save memory cache to localStorage
  private saveCache() {
    // Skip if running on server
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    
    try {
      const cache: LocalStorageCache = {};
      const now = Date.now();
      
      // Only save valid entries
      this.memoryCache.forEach((value, key) => {
        if (now - value.timestamp < this.CACHE_DURATION) {
          cache[key] = value;
        }
      });
      
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Failed to save price cache:', error);
    }
  }
  
  // Get native token price by gecko_id
  async getNativeTokenPrice(geckoId: string): Promise<number | null> {
    if (!geckoId) return null;
    
    const cacheKey = `coingecko:${geckoId}`;
    
    // Check memory cache
    const cached = this.memoryCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.price;
    }
    
    try {
      const response = await axios.get<DefiLlamaPriceResponse>(
        `${this.DEFILLAMA_API}/prices/current/${cacheKey}`
      );
      
      const data = response.data.coins[cacheKey];
      const cacheEntry: CachedPrice = {
        price: data?.price || null,
        timestamp: Date.now(),
        notFound: !data
      };
      
      this.memoryCache.set(cacheKey, cacheEntry);
      this.saveCache();
      
      return cacheEntry.price;
    } catch (error) {
      console.error(`Failed to fetch price for ${geckoId}:`, error);
      
      // Cache the failure to avoid repeated calls
      const cacheEntry: CachedPrice = {
        price: null,
        timestamp: Date.now(),
        notFound: true
      };
      
      this.memoryCache.set(cacheKey, cacheEntry);
      this.saveCache();
      
      return null;
    }
  }
  
  // Get multiple native token prices
  async getNativeTokenPrices(geckoIds: string[]): Promise<Record<string, number>> {
    const validIds = geckoIds.filter(id => id);
    if (validIds.length === 0) return {};
    
    const prices: Record<string, number> = {};
    const idsToFetch: string[] = [];
    const now = Date.now();
    
    // Check cache first
    validIds.forEach(id => {
      const cacheKey = `coingecko:${id}`;
      const cached = this.memoryCache.get(cacheKey);
      
      if (cached && now - cached.timestamp < this.CACHE_DURATION) {
        // Only add to results if price exists (not notFound)
        if (cached.price !== null) {
          prices[id] = cached.price;
        }
      } else {
        idsToFetch.push(id);
      }
    });
    
    // Fetch missing prices in batches
    if (idsToFetch.length > 0) {
      // DefiLlama supports batch requests
      const batchSize = 50;
      
      for (let i = 0; i < idsToFetch.length; i += batchSize) {
        const batch = idsToFetch.slice(i, i + batchSize);
        const coins = batch.map(id => `coingecko:${id}`).join(',');
        
        try {
          const response = await axios.get<DefiLlamaPriceResponse>(
            `${this.DEFILLAMA_API}/prices/current/${coins}`
          );
          
          batch.forEach(id => {
            const cacheKey = `coingecko:${id}`;
            const data = response.data.coins[cacheKey];
            
            if (data && data.price) {
              prices[id] = data.price;
              
              const cacheEntry: CachedPrice = {
                price: data.price,
                timestamp: Date.now()
              };
              
              this.memoryCache.set(cacheKey, cacheEntry);
            } else {
              // Cache not found result
              const cacheEntry: CachedPrice = {
                price: null,
                timestamp: Date.now(),
                notFound: true
              };
              
              this.memoryCache.set(cacheKey, cacheEntry);
            }
          });
        } catch (error) {
          console.error(`Failed to fetch batch ${Math.floor(i / batchSize) + 1}:`, error);
          
          // Cache failures to avoid repeated calls
          batch.forEach(id => {
            const cacheKey = `coingecko:${id}`;
            const cacheEntry: CachedPrice = {
              price: null,
              timestamp: Date.now(),
              notFound: true
            };
            
            this.memoryCache.set(cacheKey, cacheEntry);
          });
        }
      }
      
      // Save cache after all fetches
      this.saveCache();
    }
    
    return prices;
  }
  
  // Get token prices by contract addresses
  async getTokenPrices(chainId: number, addresses: string[]): Promise<Record<string, number>> {
    if (addresses.length === 0) return {};
    
    const chain = this.getChainName(chainId);
    if (!chain) return {};
    
    const prices: Record<string, number> = {};
    const addressesToFetch: string[] = [];
    const now = Date.now();
    
    // Check cache
    addresses.forEach(address => {
      const cacheKey = `${chain}:${address.toLowerCase()}`;
      const cached = this.memoryCache.get(cacheKey);
      
      if (cached && now - cached.timestamp < this.CACHE_DURATION) {
        // Only add to results if price exists (not notFound)
        if (cached.price !== null) {
          prices[address.toLowerCase()] = cached.price;
        }
      } else {
        addressesToFetch.push(address.toLowerCase());
      }
    });
    
    // Fetch missing prices
    if (addressesToFetch.length > 0) {
      const batchSize = 50;
      
      for (let i = 0; i < addressesToFetch.length; i += batchSize) {
        const batch = addressesToFetch.slice(i, i + batchSize);
        const coins = batch.map(addr => `${chain}:${addr}`).join(',');
        
        try {
          const response = await axios.get<DefiLlamaPriceResponse>(
            `${this.DEFILLAMA_API}/prices/current/${coins}`
          );
          
          batch.forEach(address => {
            const cacheKey = `${chain}:${address}`;
            const data = response.data.coins[cacheKey];
            
            if (data && data.price) {
              prices[address] = data.price;
              
              const cacheEntry: CachedPrice = {
                price: data.price,
                timestamp: Date.now()
              };
              
              this.memoryCache.set(cacheKey, cacheEntry);
            } else {
              // Cache not found result
              const cacheEntry: CachedPrice = {
                price: null,
                timestamp: Date.now(),
                notFound: true
              };
              
              this.memoryCache.set(cacheKey, cacheEntry);
            }
          });
        } catch (error) {
          console.error(`Failed to fetch token prices batch ${Math.floor(i / batchSize) + 1}:`, error);
          
          // Cache failures
          batch.forEach(address => {
            const cacheKey = `${chain}:${address}`;
            const cacheEntry: CachedPrice = {
              price: null,
              timestamp: Date.now(),
              notFound: true
            };
            
            this.memoryCache.set(cacheKey, cacheEntry);
          });
        }
      }
      
      this.saveCache();
    }
    
    return prices;
  }
  
  // Get single token price
  async getTokenPrice(chainId: number, address: string): Promise<number | null> {
    const prices = await this.getTokenPrices(chainId, [address]);
    return prices[address.toLowerCase()] || null;
  }
  
  // Map chainId to DefiLlama chain name
  private getChainName(chainId: number): string | null {
    const chainMap: Record<number, string> = {
      1: 'ethereum',
      137: 'polygon',
      56: 'bsc',
      42161: 'arbitrum',
      10: 'optimism',
      43114: 'avax',
      8453: 'base',
      250: 'fantom',
      25: 'cronos',
      100: 'gnosis',
      324: 'era',
      1101: 'polygon_zkevm',
      534352: 'scroll',
      59144: 'linea',
      1284: 'moonbeam',
      42220: 'celo',
      1313161554: 'aurora',
      288: 'boba',
      1088: 'metis',
      2222: 'kava',
      9001: 'evmos',
      40: 'telos',
      592: 'astar',
      66: 'okexchain',
      128: 'heco',
      321: 'kcc',
      199: 'bittorrent',
      106: 'velas',
      888: 'wanchain',
      1666600000: 'harmony',
      42262: 'oasis',
      2001: 'milkomeda',
      42170: 'arbitrum_nova',
      10000: 'smartbch',
      1818: 'cube',
      82: 'meter',
      1285: 'moonriver',
      4689: 'iotex',
      416: 'sx',
      11297108109: 'palm',
      53935: 'dfk',
      71402: 'godwoken',
      2046399126: 'skale_europa',
      5050: 'xlc',
      11155111: 'sepolia',
      421613: 'arbitrum_goerli',
      80001: 'mumbai',
      97: 'bsc_testnet',
      5: 'goerli'
    };
    
    return chainMap[chainId] || null;
  }
  
  // Force refresh a specific price
  async refreshPrice(geckoId: string): Promise<number | null> {
    const cacheKey = `coingecko:${geckoId}`;
    this.memoryCache.delete(cacheKey);
    return this.getNativeTokenPrice(geckoId);
  }
  
  // Force refresh a token price
  async refreshTokenPrice(chainId: number, address: string): Promise<number | null> {
    const chain = this.getChainName(chainId);
    if (!chain) return null;
    
    const cacheKey = `${chain}:${address.toLowerCase()}`;
    this.memoryCache.delete(cacheKey);
    
    const prices = await this.getTokenPrices(chainId, [address]);
    return prices[address.toLowerCase()] || null;
  }
  
  // Clear all cache
  clearCache() {
    this.memoryCache.clear();
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(this.CACHE_KEY);
    }
  }
  
  // Get cache statistics
  getCacheStats(): { 
    total: number; 
    valid: number; 
    expired: number; 
    notFound: number;
    withPrice: number;
  } {
    const now = Date.now();
    let valid = 0;
    let expired = 0;
    let notFound = 0;
    let withPrice = 0;
    
    this.memoryCache.forEach(value => {
      if (now - value.timestamp < this.CACHE_DURATION) {
        valid++;
        if (value.notFound) {
          notFound++;
        } else if (value.price !== null) {
          withPrice++;
        }
      } else {
        expired++;
      }
    });
    
    return {
      total: this.memoryCache.size,
      valid,
      expired,
      notFound,
      withPrice
    };
  }
  
  // Manual cache cleanup
  cleanupCache() {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.memoryCache.forEach((value, key) => {
      if (now - value.timestamp >= this.CACHE_DURATION) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.memoryCache.delete(key));
    
    if (keysToDelete.length > 0) {
      this.saveCache();
    }
  }
}

export const priceService = PriceService.getInstance();

// Auto cleanup expired cache every hour
if (typeof window !== 'undefined') {
  setInterval(() => {
    priceService.cleanupCache();
  }, 60 * 60 * 1000);
}